import React, {
    useEffect,
    useRef
}
from "react";

import * as am4core
from "@amcharts/amcharts4/core";

import * as am4charts
from "@amcharts/amcharts4/charts";

import am4themes_animated
from "@amcharts/amcharts4/themes/animated";

import { LICENCE_KEY }
from "../../../../constants/charts";


am4core.useTheme(
    am4themes_animated
);

am4core.addLicense(
    LICENCE_KEY
);


export default function InsightDashboardComplianceChart({
    data = [],
    clickFunction
})
{

    const chartRef =
        useRef(null);


    //--------------------------------------------------------
    // Compliance group
    //--------------------------------------------------------

    function getComplianceGroup(name)
    {
        const value =
            (name || "")
                .trim()
                .toUpperCase();


        //----------------------------------------------------
        // Definite compliance issue
        //----------------------------------------------------

        if(
            value.includes("NON-COMPLIANT")
            ||
            value.includes("NON COMPLIANT")
            ||
            value.includes("FAIL")
            ||
            value.includes("BREACH")
            ||
            value.includes("VIOLATION")
        )
        {
            return "Issue";
        }


        //----------------------------------------------------
        // Confirmed compliant
        //----------------------------------------------------

        if(
            value === "COMPLIANT"
            ||
            value === "PASS"
            ||
            value === "POSITIVE"
        )
        {
            return "Compliant";
        }


        //----------------------------------------------------
        // Unknown / no useful determination
        //----------------------------------------------------

        if(
            value === ""
            ||
            value === "NONE"
            ||
            value === "NO CONCERN"
            ||
            value === "N/A"
            ||
            value === "UNKNOWN"
            ||
            value === "UNCERTAIN"
            ||
            value === "UNCLEAR"
            ||
            value === "NOT VERIFIED"
        )
        {
            return "Unknown";
        }


        //----------------------------------------------------
        // Anything else requires review
        //----------------------------------------------------

        return "Review";
    }


    //--------------------------------------------------------
    // Get colour
    //--------------------------------------------------------

    function getColour(name)
    {
        switch(name)
        {
            case "Issue":
                return "#e84d4f";

            case "Review":
                return "#f4b73f";

            case "Compliant":
                return "#38b878";

            case "Unknown":
            default:
                return "#9ca3af";
        }
    }


    //--------------------------------------------------------
    // Map / group data
    //--------------------------------------------------------

    function mapData(rows)
    {
        const groups =
        {
            Issue: 0,
            Review: 0,
            Compliant: 0,
            Unknown: 0
        };


        (
            rows
            ||
            []
        ).forEach(
            (item) =>
            {
                const group =
                    getComplianceGroup(
                        item.name
                    );


                groups[group] +=
                    item.total
                    ||
                    0;
            }
        );


        //----------------------------------------------------
        // Return in deliberate RAG order
        //----------------------------------------------------

        return [
            "Issue",
            "Review",
            "Compliant",
            "Unknown"
        ]
        .map(
            (name) =>
            {
                return {
                    name:
                        name,

                    value:
                        groups[name],

                    colour:
                        getColour(
                            name
                        ),

                    originalData:
                    {
                        name:
                            name,

                        total:
                            groups[name]
                    }
                };
            }
        )
        .filter(
            (item) =>
            {
                return item.value > 0;
            }
        );
    }


    //--------------------------------------------------------
    // Grouped data
    //--------------------------------------------------------

    const groupedData =
        mapData(
            data
        );


    //--------------------------------------------------------
    // Total
    //--------------------------------------------------------

    const total =
        groupedData.reduce(
            (sum, item) =>
            {
                return (
                    sum
                    +
                    (
                        item.value
                        ||
                        0
                    )
                );
            },
            0
        );


    //--------------------------------------------------------
    // Create chart
    //--------------------------------------------------------

    useEffect(
        () =>
        {
            if(
                !data
                ||
                data.length === 0
            )
            {
                return;
            }


            const chart =
                am4core.create(
                    "insight-dashboard-compliance-chart",
                    am4charts.PieChart
                );


            chart.padding(
                0,
                0,
                0,
                0
            );


            chart.innerRadius =
                am4core.percent(
                    62
                );


            chart.data =
                mapData(
                    data
                );


            //------------------------------------------------
            // Series
            //------------------------------------------------

            const series =
                chart.series.push(
                    new am4charts.PieSeries()
                );


            series.dataFields.value =
                "value";

            series.dataFields.category =
                "name";


            //------------------------------------------------
            // Slice appearance
            //------------------------------------------------

            series.slices.template.stroke =
                am4core.color(
                    "#ffffff"
                );

            series.slices.template.strokeWidth =
                2;

            series.slices.template.strokeOpacity =
                0.9;


            series.slices.template
                .propertyFields.fill =
                    "colour";

            series.slices.template
                .propertyFields.stroke =
                    "colour";


            //------------------------------------------------
            // Tooltip
            //------------------------------------------------

            series.slices.template.tooltipText =
                "{name}\n[bold]{value} insights[/]";


            //------------------------------------------------
            // Labels
            //------------------------------------------------

            series.labels.template.disabled =
                true;

            series.ticks.template.disabled =
                true;


            //------------------------------------------------
            // Hover
            //------------------------------------------------

            const hover =
                series.slices.template
                    .states
                    .getKey(
                        "hover"
                    );


            hover.properties.scale =
                1.04;


            //------------------------------------------------
            // Click
            //------------------------------------------------

            series.slices.template
                .events
                .on(
                    "hit",
                    function(event)
                    {
                        if(!clickFunction)
                        {
                            return;
                        }


                        const item =
                            event.target
                                .dataItem
                                .dataContext;


                        if(!item)
                        {
                            return;
                        }


                        clickFunction(
                            item.originalData
                        );
                    }
                );


            //------------------------------------------------
            // Save reference
            //------------------------------------------------

            chartRef.current =
                chart;


            //------------------------------------------------
            // Cleanup
            //------------------------------------------------

            return () =>
            {
                chart.dispose();

                chartRef.current =
                    null;
            };
        },
        [
            data,
            clickFunction
        ]
    );


    //--------------------------------------------------------
    // Render
    //--------------------------------------------------------

    return (

        <div style={containerStyle}>

            <div style={chartAreaStyle}>

                <div
                    id="insight-dashboard-compliance-chart"

                    style={{
                        width: "100%",
                        height: "100%"
                    }}
                />


                <div style={centreStyle}>

                    <div style={centreValueStyle}>
                        {total.toLocaleString()}
                    </div>

                    <div style={centreLabelStyle}>
                        ASSESSED
                    </div>

                </div>

            </div>


            <div style={legendStyle}>

                {
                    groupedData.map(
                        (item, index) =>
                        {
                            return (

                                <div
                                    key={
                                        item.name
                                        +
                                        "-"
                                        +
                                        index
                                    }

                                    style={legendItemStyle}
                                >

                                    <div
                                        style={{
                                            ...legendDotStyle,

                                            background:
                                                item.colour
                                        }}
                                    />


                                    <div style={legendNameStyle}>
                                        {item.name}
                                    </div>


                                    <div style={legendValueStyle}>
                                        {
                                            (
                                                item.value
                                                ||
                                                0
                                            ).toLocaleString()
                                        }
                                    </div>

                                </div>

                            );
                        }
                    )
                }

            </div>

        </div>

    );
}


//--------------------------------------------------------
// Styles
//--------------------------------------------------------

const containerStyle =
{
    width: "100%",
    height: "100%",

    minHeight: 0,

    display: "flex",
    alignItems: "center",

    gap: "12px"
};


const chartAreaStyle =
{
    position: "relative",

    flex: "0 0 48%",

    height: "100%",

    minWidth: 0,
    minHeight: 0
};


const centreStyle =
{
    position: "absolute",

    left: "50%",
    top: "50%",

    transform:
        "translate(-50%, -50%)",

    pointerEvents: "none",

    width: "58px",
    height: "58px",

    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",

    background:
        "rgba(255,255,255,0.78)",

    border:
        "1px solid rgba(255,255,255,0.95)",

    borderRadius: "50%",

    boxShadow:
        "0 5px 16px rgba(30,42,58,0.10)",

    backdropFilter:
        "blur(6px)"
};


const centreValueStyle =
{
    color: "#28313b",

    fontSize: "15px",
    lineHeight: "1",

    fontWeight: "800"
};


const centreLabelStyle =
{
    marginTop: "3px",

    color: "#909baa",

    fontSize: "6.5px",

    fontWeight: "800",

    letterSpacing: "0.8px"
};


const legendStyle =
{
    flex: 1,

    minWidth: 0,

    display: "flex",
    flexDirection: "column",

    justifyContent: "center",

    gap: "10px"
};


const legendItemStyle =
{
    display: "grid",

    gridTemplateColumns:
        "8px minmax(0, 1fr) auto",

    alignItems: "center",

    gap: "7px"
};


const legendDotStyle =
{
    width: "7px",
    height: "7px",

    borderRadius: "50%"
};


const legendNameStyle =
{
    minWidth: 0,

    overflow: "hidden",

    color: "#626d7b",

    fontSize: "8px",
    fontWeight: "600",

    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
};


const legendValueStyle =
{
    color: "#29313b",

    fontSize: "9px",
    fontWeight: "800"
};